/**
 * LISTS-01 core — the shared machinery behind the ESC-style list views under
 * Reports (Seth's Dispatch List / Invoice List screenshots): a 30-day default
 * date range, free-text search, refine filters, live count + totals, CSV
 * export, and SAVED VIEWS in ESC's two tiers — Custom Views (shared with the
 * whole office) and Personal Views. Competitor frame: ServiceTitan's list-view
 * toggle + HubSpot saved views; ESC's separate menu-per-entity IA rejected —
 * these live as report cards, the nav stays lean.
 */
import { useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, Calendar, ChevronDown, Download, Trash2, X, Columns3, Layers, Sigma, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { SlideOver } from "@/components/ui/SlideOver";

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function isoToday(): string {
  return isoDaysAgo(0);
}

// ---------------------------------------------------------------------------
// Saved views (pegasus_saved_view)
// ---------------------------------------------------------------------------

export interface SavedView {
  id: string;
  entity: string;
  name: string;
  filters: Record<string, unknown>;
  scope: "personal" | "shared";
  created_by: string | null;
}

export function useSavedViews(entity: string) {
  return useQuery({
    queryKey: ["saved_views", entity],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<SavedView[]> => {
      const { data: u } = await supabase.auth.getUser();
      const me = u?.user?.email ?? "";
      const { data, error } = await supabase
        .from("pegasus_saved_view")
        .select("*")
        .eq("entity", entity)
        .order("created_at");
      if (error) throw error;
      // Shared views for everyone; personal only for their owner.
      return ((data ?? []) as SavedView[]).filter(
        (v) => v.scope === "shared" || v.created_by === me,
      );
    },
  });
}

export function useSaveView(entity: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      name: string;
      scope: "personal" | "shared";
      filters: Record<string, unknown>;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_saved_view").insert({
        entity,
        name: v.name.trim(),
        scope: v.scope,
        filters: v.filters,
        created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved_views", entity] }),
  });
}

export function useDeleteView(entity: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_saved_view").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved_views", entity] }),
  });
}

/** The saved-views bar: chips to apply a view, save-current, delete-own. */
export function SavedViewsBar({
  entity,
  currentFilters,
  onApply,
}: {
  entity: string;
  currentFilters: Record<string, unknown>;
  onApply: (filters: Record<string, unknown>) => void;
}) {
  const { data: views } = useSavedViews(entity);
  const save = useSaveView(entity);
  const del = useDeleteView(entity);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"personal" | "shared">("personal");

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {(views ?? []).map((v) => (
        <span key={v.id} className="group/view inline-flex items-center">
          <button
            type="button"
            onClick={() => onApply(v.filters)}
            title={v.scope === "shared" ? "Custom view (whole office)" : "Personal view (just you)"}
            className={cn(
              "rounded-l-full border border-r-0 px-2.5 py-1 text-xs font-medium transition-colors",
              v.scope === "shared"
                ? "border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100"
                : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
            )}
          >
            {v.name}
          </button>
          <button
            type="button"
            onClick={() => del.mutate(v.id)}
            title="Delete this view"
            className={cn(
              "rounded-r-full border border-l-0 px-1 py-1 text-slate-300 transition-colors hover:text-red-500",
              v.scope === "shared" ? "border-brand-200 bg-brand-50" : "border-hairline bg-white",
            )}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </span>
      ))}

      {saving ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            save.mutate(
              { name, scope, filters: currentFilters },
              { onSuccess: () => { setSaving(false); setName(""); } },
            );
          }}
          className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white py-0.5 pl-2 pr-1"
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="View name…"
            className="w-28 bg-transparent text-xs focus:outline-none"
          />
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as "personal" | "shared")}
            className="rounded bg-slate-50 px-1 py-0.5 text-[11px] text-slate-600"
            title="Personal = just you · Custom = the whole office sees it"
          >
            <option value="personal">Personal</option>
            <option value="shared">Custom (office)</option>
          </select>
          <button
            type="submit"
            disabled={!name.trim() || save.isPending}
            className="rounded-full bg-brand-700 px-2 py-0.5 text-[11px] font-semibold text-white disabled:bg-brand-300"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setSaving(false)}
            className="p-0.5 text-slate-400 hover:text-slate-600"
          >
            <X className="h-3 w-3" />
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setSaving(true)}
          title="Save the current filters as a view — Personal (just you) or Custom (whole office)"
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-700"
        >
          <Bookmark className="h-3 w-3" />
          Save view
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSV export — client-side from the filtered rows.
// ---------------------------------------------------------------------------

export function downloadCsv(
  filename: string,
  headers: string[],
  rows: (string | number | null | undefined)[][],
) {
  const esc = (v: string | number | null | undefined) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function CsvButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Download what's on screen (current filters) as a CSV for Excel"
      className="inline-flex h-9 items-center gap-1.5 rounded-full border border-hairline bg-white px-3.5 text-[12.5px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:border-slate-300"
    >
      <Download className="h-3.5 w-3.5" />
      CSV
    </button>
  );
}

// ---------------------------------------------------------------------------
// Column sorting (Seth 2026-06-10: "heads clickable so I can sort by that
// column — carry it anywhere it makes sense"). One hook + one <Th> so every
// list sorts identically: first click = ascending, second flips, arrow shows.
// ---------------------------------------------------------------------------

export function useSort<T>(defaultKey: string, defaultDir: "asc" | "desc" = "desc") {
  const [key, setKey] = useState(defaultKey);
  const [dir, setDir] = useState<"asc" | "desc">(defaultDir);
  const toggle = (k: string) => {
    if (k === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setKey(k);
      setDir("asc");
    }
  };
  const sortRows = (
    rows: T[],
    accessors: Record<string, (r: T) => string | number | null | undefined>,
  ): T[] => {
    const acc = accessors[key];
    if (!acc) return rows;
    return [...rows].sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      // Nulls sink to the bottom in either direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return dir === "asc" ? cmp : -cmp;
    });
  };
  return { key, dir, toggle, sortRows };
}

export function Th({
  id,
  sort,
  children,
  align = "left",
}: {
  id: string;
  sort: { key: string; dir: "asc" | "desc"; toggle: (k: string) => void };
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  const active = sort.key === id;
  return (
    <th
      className={cn(
        // Sticky so the office can sort + read column heads while scrolled deep
        // into thousands of rows (ServiceTitan / Airtable pattern).
        "sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-2.5 text-[12px] font-semibold",
        align === "right" && "text-right",
      )}
    >
      <button
        type="button"
        onClick={() => sort.toggle(id)}
        title="Sort by this column (click again to flip)"
        className={cn(
          "group/sort inline-flex items-center gap-1 transition-colors hover:text-slate-900",
          align === "right" && "flex-row-reverse",
          active ? "text-slate-900" : "text-slate-500",
        )}
      >
        {children}
        {/* Active = solid brand arrow; idle = faint arrow on hover so it reads as sortable. */}
        <span
          className={cn(
            "text-[9px] transition-opacity",
            active ? "text-brand-600 opacity-100" : "opacity-0 group-hover/sort:opacity-40",
          )}
        >
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "▼"}
        </span>
      </button>
    </th>
  );
}

/** Shared toolbar field shells so both lists look identical. */
export function ToolbarField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-slate-500">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

export const inputCls =
  "rounded-md border border-hairline bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30";

// ---------------------------------------------------------------------------
// TOOLBAR-REFRESH — uniform pill controls + a date-range pill + a Views menu so
// the list toolbar reads as one tidy command row instead of ragged rows of
// mismatched inputs. (Linear/Airtable: one search, one range pill, one filter
// entry, views behind a menu.) Shared so every list adopts the same bar.
// ---------------------------------------------------------------------------

/** Pill button — uniform height/radius for date-range + the views menu. */
export const pillCls =
  "inline-flex h-9 items-center gap-1.5 rounded-full border border-hairline bg-white px-3.5 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-400/25";
/** Native <select> styled to match the pills (quick filters). */
export const pillSelectCls =
  "h-9 cursor-pointer rounded-full border border-hairline bg-white pl-3.5 pr-8 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-300 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/25";
/** Text input styled to match the pills (e.g. city / state / zip quick filters). */
export const pillInputCls =
  "h-9 rounded-full border border-hairline bg-white px-3.5 text-[12.5px] text-slate-700 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/25";

/** The standard list search box — leading icon + flex-1; every list toolbar uses it. */
export function ListSearch({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative min-w-[240px] flex-1">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-full border border-transparent bg-slate-100/80 pl-9 pr-3 text-[13.5px] text-slate-900 placeholder:text-slate-500 transition-all duration-150 ease-out-quart hover:bg-slate-100 focus:border-brand-500/60 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-500/15"
      />
    </div>
  );
}

const RANGE_PRESETS: { label: string; days: number }[] = [
  { label: "Last 7 Days", days: 7 },
  { label: "Last 30 Days", days: 30 },
  { label: "Last 90 Days", days: 90 },
  { label: "Last 12 Months", days: 365 },
];

/** One date-range control: a pill showing the range + a popover with quick
 *  presets and custom From/To. Replaces two raw <input type=date> — the main
 *  toolbar-clutter culprit. Labels itself with the preset name when it matches. */
export function DateRangeControl({ from, to, onChange }: { from: string; to: string; onChange: (from: string, to: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const preset = to === isoToday() ? RANGE_PRESETS.find((p) => from === isoDaysAgo(p.days)) : undefined;
  const label = preset
    ? preset.label
    : `${from ? format(parseISO(from), "MMM d") : "…"} – ${to ? format(parseISO(to), "MMM d, yyyy") : "…"}`;
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className={pillCls} title="Change the date range">
        <Calendar className="h-3.5 w-3.5 text-slate-400" />
        {label}
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-xl border border-hairline bg-white p-3 shadow-lg">
          <div className="grid grid-cols-2 gap-1.5">
            {RANGE_PRESETS.map((p) => {
              const on = preset?.days === p.days;
              return (
                <button key={p.days} type="button"
                  onClick={() => { onChange(isoDaysAgo(p.days), isoToday()); setOpen(false); }}
                  className={cn("rounded-md border px-2 py-1.5 text-xs font-medium transition-colors",
                    on ? "border-brand-200 bg-brand-50 text-brand-700" : "border-hairline text-slate-600 hover:bg-slate-50")}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
            <label className="flex items-center justify-between gap-2 text-xs text-slate-500">From
              <input type="date" value={from} onChange={(e) => onChange(e.target.value, to)} className={inputCls} /></label>
            <label className="flex items-center justify-between gap-2 text-xs text-slate-500">To
              <input type="date" value={to} onChange={(e) => onChange(from, e.target.value)} className={inputCls} /></label>
          </div>
        </div>
      )}
    </div>
  );
}

/** Saved views in a tidy dropdown (replaces the inline dashed-pill bar): apply,
 *  delete-own, and save-current (Personal / Custom-office). */
export function ViewsMenu({ entity, currentFilters, onApply }: { entity: string; currentFilters: Record<string, unknown>; onApply: (filters: Record<string, unknown>) => void }) {
  const { data: views } = useSavedViews(entity);
  const save = useSaveView(entity);
  const del = useDeleteView(entity);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"personal" | "shared">("personal");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setSaving(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const list = views ?? [];
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} title="Saved views"
        className={cn(pillCls, list.length > 0 && "border-brand-200 bg-brand-50 text-brand-700")}>
        <Bookmark className="h-3.5 w-3.5" /> Views
        {list.length > 0 && <span className="rounded bg-brand-100 px-1 text-[10px] font-bold">{list.length}</span>}
        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-64 rounded-xl border border-hairline bg-white p-2 shadow-lg">
          {list.length > 0 ? (
            <div className="space-y-0.5">
              {list.map((v) => (
                <div key={v.id} className="group flex items-center gap-1 rounded-md hover:bg-slate-50">
                  <button type="button" onClick={() => { onApply(v.filters); setOpen(false); }}
                    title={v.scope === "shared" ? "Custom view (whole office)" : "Personal view (just you)"}
                    className="flex min-w-0 flex-1 items-center gap-1.5 px-2 py-1.5 text-left text-sm text-slate-700">
                    <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", v.scope === "shared" ? "bg-brand-500" : "bg-slate-300")} />
                    <span className="truncate">{v.name}</span>
                  </button>
                  <button type="button" onClick={() => del.mutate(v.id)} title="Delete this view"
                    className="mr-1 shrink-0 text-slate-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="px-2 py-3 text-center text-xs text-slate-400">No saved views yet.</p>
          )}
          <div className="mt-1 border-t border-slate-100 pt-1">
            {saving ? (
              <form onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; save.mutate({ name, scope, filters: currentFilters }, { onSuccess: () => { setSaving(false); setName(""); } }); }}
                className="space-y-1.5 p-1.5">
                <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="View name…"
                  className="w-full rounded-md border border-hairline px-2 py-1.5 text-xs focus:border-brand-400 focus:outline-none" />
                <div className="flex items-center gap-1.5">
                  <select value={scope} onChange={(e) => setScope(e.target.value as "personal" | "shared")}
                    className="min-w-0 flex-1 rounded-md border border-hairline bg-white px-1.5 py-1 text-[11px] text-slate-600">
                    <option value="personal">Personal (just me)</option>
                    <option value="shared">Custom (office)</option>
                  </select>
                  <button type="submit" disabled={!name.trim() || save.isPending}
                    className="shrink-0 rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white disabled:bg-brand-300">Save</button>
                </div>
              </form>
            ) : (
              <button type="button" onClick={() => setSaving(true)}
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm font-medium text-slate-600 hover:bg-slate-50">
                <Bookmark className="h-3.5 w-3.5 text-slate-400" /> Save current view…
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// LISTS-ENGINE — the reusable "Select Fields / Group / Total" capability that
// makes a list universal (ESC's Agreement-List toolkit, reborn). A list passes
// a FIELD CATALOG; the office chooses which columns show, an optional Group-by,
// and which numeric columns to total. One catalog per entity → every list gets
// the same power for free, and the AI ("Ask Pegasus") fills out the SAME shape.
// ===========================================================================

export type FieldType = "text" | "number" | "date" | "bool" | "enum";

export interface ListField<T> {
  id: string;
  label: string;
  /** Raw value — used for sort, group, total and CSV. */
  value: (r: T) => string | number | null | undefined;
  /** Optional rich cell (badges, links). Falls back to value(). */
  render?: (r: T) => React.ReactNode;
  numeric?: boolean;        // right-align + summable
  groupable?: boolean;      // can be a Group-by dimension
  totalable?: boolean;      // can be Σ-summed (implies numeric)
  defaultVisible?: boolean; // shown out of the box (default true)
  align?: "left" | "right";
  // ── filter metadata (filter-by-any-field + the Ask Pegasus semantic layer) ──
  col?: string;             // the DB column to filter on (defaults to id)
  type?: FieldType;         // drives the operator choices (defaults to "text")
  filterable?: boolean;     // offered in the Filters slide-out
  options?: { value: string; label: string }[]; // for type "enum"
}

// Operators offered per field type. noValue ops (is empty / is yes) need no input.
export const FILTER_OPS: Record<FieldType, { op: string; label: string; noValue?: boolean }[]> = {
  text: [
    { op: "contains", label: "contains" }, { op: "equals", label: "is" },
    { op: "starts", label: "starts with" }, { op: "empty", label: "is empty", noValue: true },
    { op: "notempty", label: "is not empty", noValue: true },
  ],
  number: [
    { op: "eq", label: "=" }, { op: "gte", label: "≥" }, { op: "lte", label: "≤" }, { op: "between", label: "between" },
  ],
  date: [
    { op: "after", label: "on / after" }, { op: "before", label: "on / before" }, { op: "on", label: "on" }, { op: "between", label: "between" },
  ],
  bool: [{ op: "true", label: "is yes", noValue: true }, { op: "false", label: "is no", noValue: true }],
  enum: [{ op: "is", label: "is" }],
};

export interface FilterCond { id: string; field: string; op: string; value: string; value2?: string }

/** Apply a list of filter conditions to a PostgREST query (server-side). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyFilters<Q>(query: Q, conds: FilterCond[], fields: ListField<unknown>[]): Q {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q: any = query;
  const byId = new Map(fields.map((f) => [f.id, f]));
  for (const c of conds) {
    const f = byId.get(c.field);
    if (!f) continue;
    const col = f.col ?? f.id;
    const type = f.type ?? "text";
    const v = c.value;
    const num = (s: string) => Number(s);
    switch (c.op) {
      case "contains": q = q.ilike(col, `%${v}%`); break;
      case "equals": q = type === "number" ? q.eq(col, num(v)) : q.ilike(col, v); break;
      case "starts": q = q.ilike(col, `${v}%`); break;
      case "empty": q = q.is(col, null); break;
      case "notempty": q = q.not(col, "is", null); break;
      case "eq": q = q.eq(col, num(v)); break;
      case "gte": q = q.gte(col, type === "number" ? num(v) : v); break;
      case "lte": q = q.lte(col, type === "number" ? num(v) : v); break;
      case "between": q = q.gte(col, type === "number" ? num(v) : v).lte(col, type === "number" ? num(c.value2 ?? v) : (c.value2 ?? v)); break;
      case "after": q = q.gte(col, v); break;
      case "before": q = q.lte(col, v); break;
      case "on": q = q.eq(col, v); break;
      case "true": q = q.eq(col, true); break;
      case "false": q = q.eq(col, false); break;
      case "is": q = q.eq(col, v); break;
    }
  }
  return q as Q;
}

/** A short chip label for an active condition. */
export function condLabel(c: FilterCond, fields: ListField<unknown>[]): string {
  const f = fields.find((x) => x.id === c.field);
  const label = f?.label ?? c.field;
  const opLabel = FILTER_OPS[f?.type ?? "text"].find((o) => o.op === c.op)?.label ?? c.op;
  const noVal = FILTER_OPS[f?.type ?? "text"].find((o) => o.op === c.op)?.noValue;
  if (noVal) return `${label} ${opLabel}`;
  if (c.op === "between") return `${label} ${c.value}–${c.value2 ?? ""}`;
  // enum: show the option label
  const optLabel = f?.options?.find((o) => o.value === c.value)?.label ?? c.value;
  return `${label} ${opLabel} ${optLabel}`;
}

export interface ColumnState {
  visible: string[];                 // ordered, visible column ids
  groupBy: string | null;            // group dimension id
  totals: string[];                  // column ids being Σ-summed
  toggle: (id: string) => void;
  setGroupBy: (id: string | null) => void;
  toggleTotal: (id: string) => void;
  reset: () => void;
}

/** Column/group/total choices, persisted per entity (the choice sticks). */
export function useColumns<T>(entity: string, fields: ListField<T>[]): ColumnState {
  // v2: bumped when an entity's default columns change so the new defaults take
  // effect instead of a stale saved set.
  const KEY = `pegasus.cols.v2.${entity}`;
  const def = () => fields.filter((f) => f.defaultVisible !== false).map((f) => f.id);
  const load = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
  };
  const init = load();
  const [visible, setVisible] = useState<string[]>(
    Array.isArray(init?.visible) && init.visible.length ? init.visible : def(),
  );
  const [groupBy, setGroupByState] = useState<string | null>(init?.groupBy ?? null);
  const [totals, setTotals] = useState<string[]>(Array.isArray(init?.totals) ? init.totals : []);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify({ visible, groupBy, totals })); } catch { /* ignore */ }
  }, [KEY, visible, groupBy, totals]);

  return {
    visible, groupBy, totals,
    toggle: (id) => setVisible((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id])),
    setGroupBy: (id) => setGroupByState(id),
    toggleTotal: (id) => setTotals((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id])),
    reset: () => { setVisible(def()); setGroupByState(null); setTotals([]); },
  };
}

/** The "Fields" slide-out — show/hide any column, pick a Group-by, toggle Σ
 *  totals (ESC's Select Fields, modernized). Searchable for long catalogs. */
export function ColumnsButton<T>({ fields, cols }: { fields: ListField<T>[]; cols: ColumnState }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const groupables = fields.filter((f) => f.groupable);
  const matches = q.trim() ? fields.filter((f) => f.label.toLowerCase().includes(q.trim().toLowerCase())) : fields;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Choose columns, group and total — like ESC's Select Fields"
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-[12.5px] font-semibold transition-colors",
          cols.groupBy || cols.totals.length
            ? "border-brand-200 bg-brand-50 text-brand-700"
            : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
        )}
      >
        <Columns3 className="h-3.5 w-3.5" /> Fields
        {(cols.groupBy || cols.totals.length > 0) && (
          <span className="rounded bg-brand-100 px-1 text-[10px] font-semibold">{cols.groupBy ? "grouped" : "Σ"}</span>
        )}
      </button>

      <SlideOver
        open={open}
        onClose={() => setOpen(false)}
        title="Choose fields"
        subtitle="Show, group and total any column"
        size="sm"
        footer={
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span><span className="font-semibold text-slate-700">{cols.visible.length}</span> shown · {fields.length} available</span>
            <button type="button" onClick={cols.reset} className="font-medium text-slate-400 hover:text-slate-700">Reset to default</button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-slate-500">
              <Layers className="h-3 w-3" /> Group by
            </div>
            <select value={cols.groupBy ?? ""} onChange={(e) => cols.setGroupBy(e.target.value || null)} className={cn(inputCls, "w-full")}>
              <option value="">No grouping</option>
              {groupables.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2 rounded-md border border-hairline bg-white px-2.5 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a field…" className="w-full bg-transparent text-xs focus:outline-none" />
          </div>

          <div className="space-y-0.5">
            {matches.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-2 rounded-md px-1.5 py-1.5 hover:bg-slate-50">
                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={cols.visible.includes(f.id)} onChange={() => cols.toggle(f.id)} className="h-4 w-4 accent-brand-700" />
                  <span className="truncate">{f.label}</span>
                </label>
                {f.totalable && (
                  <button type="button" onClick={() => cols.toggleTotal(f.id)} title={`Total the ${f.label} column`}
                    className={cn("inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors",
                      cols.totals.includes(f.id) ? "bg-emerald-50 text-emerald-700" : "text-slate-300 hover:bg-slate-100 hover:text-slate-500")}>
                    <Sigma className="h-3 w-3" /> total
                  </button>
                )}
              </div>
            ))}
            {matches.length === 0 && <p className="px-1.5 py-3 text-center text-xs text-slate-400">No field matches “{q}”.</p>}
          </div>
        </div>
      </SlideOver>
    </>
  );
}

/** The "Filters" slide-out — add a condition on ANY filterable field (ESC's
 *  Filters tab, modernized). Active conditions list inside; chips in the bar. */
export function FiltersButton<T>({ fields, conds, onChange }: { fields: ListField<T>[]; conds: FilterCond[]; onChange: (c: FilterCond[]) => void }) {
  const [open, setOpen] = useState(false);
  const filterable = fields.filter((f) => f.filterable);
  const [field, setField] = useState("");
  const [op, setOp] = useState("");
  const [val, setVal] = useState("");
  const [val2, setVal2] = useState("");

  const ff = filterable.find((f) => f.id === field);
  const type = ff?.type ?? "text";
  const ops = FILTER_OPS[type];
  const opMeta = ops.find((o) => o.op === op);
  const needsValue = !!op && !opMeta?.noValue;
  const needsTwo = op === "between";

  const reset = () => { setField(""); setOp(""); setVal(""); setVal2(""); };
  const add = () => {
    if (!field || !op || (needsValue && !val.trim())) return;
    onChange([...conds, { id: crypto.randomUUID(), field, op, value: val.trim(), value2: needsTwo ? val2.trim() : undefined }]);
    reset();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Filter by any field — like ESC's Filters tab"
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-[12.5px] font-semibold transition-colors",
          conds.length ? "border-brand-200 bg-brand-50 text-brand-700" : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
        )}
      >
        <Layers className="h-3.5 w-3.5" /> Filters
        {conds.length > 0 && <span className="rounded bg-brand-100 px-1 text-[10px] font-semibold">{conds.length}</span>}
      </button>

      <SlideOver
        open={open} onClose={() => setOpen(false)} title="Filters" subtitle="Filter by any field" size="sm"
        footer={conds.length > 0 ? (
          <button type="button" onClick={() => onChange([])} className="text-xs font-medium text-slate-400 hover:text-red-600">Clear all filters</button>
        ) : undefined}
      >
        <div className="space-y-4">
          {conds.length > 0 && (
            <div className="space-y-1.5">
              {conds.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-sm text-slate-700">
                  <span className="min-w-0 truncate">{condLabel(c, fields as ListField<unknown>[])}</span>
                  <button type="button" onClick={() => onChange(conds.filter((x) => x.id !== c.id))} className="shrink-0 text-slate-300 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2 rounded-lg border border-dashed border-slate-300 p-3">
            <div className="text-[12px] font-semibold text-slate-500">Add a filter</div>
            <select value={field} onChange={(e) => { setField(e.target.value); setOp(""); setVal(""); }} className={cn(inputCls, "w-full")}>
              <option value="">Choose a field…</option>
              {filterable.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
            {field && (
              <select value={op} onChange={(e) => { setOp(e.target.value); setVal(""); setVal2(""); }} className={cn(inputCls, "w-full")}>
                <option value="">Condition…</option>
                {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
              </select>
            )}
            {needsValue && type === "enum" && (
              <select value={val} onChange={(e) => setVal(e.target.value)} className={cn(inputCls, "w-full")}>
                <option value="">Value…</option>
                {(ff?.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            {needsValue && type !== "enum" && (
              <div className="flex items-center gap-2">
                <input
                  type={type === "date" ? "date" : type === "number" ? "number" : "text"}
                  value={val} onChange={(e) => setVal(e.target.value)} placeholder="Value"
                  className={cn(inputCls, "w-full")}
                />
                {needsTwo && (
                  <input
                    type={type === "date" ? "date" : "number"} value={val2} onChange={(e) => setVal2(e.target.value)} placeholder="and"
                    className={cn(inputCls, "w-full")}
                  />
                )}
              </div>
            )}
            <button
              type="button" onClick={add}
              disabled={!field || !op || (needsValue && !val.trim())}
              className="w-full rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
            >
              Add filter
            </button>
          </div>
        </div>
      </SlideOver>
    </>
  );
}

/** Active filters as removable chips for the toolbar. */
export function FilterChips<T>({ conds, fields, onChange }: { conds: FilterCond[]; fields: ListField<T>[]; onChange: (c: FilterCond[]) => void }) {
  if (!conds.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {conds.map((c) => (
        <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
          {condLabel(c, fields as ListField<unknown>[])}
          <button type="button" onClick={() => onChange(conds.filter((x) => x.id !== c.id))} className="text-brand-400 hover:text-brand-700"><X className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
}

const fmtCell = (v: string | number | null | undefined, numeric?: boolean): string => {
  if (v == null || v === "") return numeric ? "0" : "—";
  if (numeric && typeof v === "number") return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return String(v);
};

/**
 * The generic list table — renders only the chosen columns, sorts via the
 * shared <Th>, and when a Group-by is set, partitions into group blocks with a
 * per-group count + Σ subtotals and a grand-total footer. Row click selects.
 */
export function ListTable<T>({
  rows, fields, cols, sort, getKey, onRowClick, selectedKey, emptyText = "Nothing matches these filters.",
}: {
  rows: T[];
  fields: ListField<T>[];
  cols: ColumnState;
  sort: ReturnType<typeof useSort<T>>;
  getKey: (r: T) => string;
  onRowClick?: (r: T) => void;
  selectedKey?: string | null;
  emptyText?: string;
}) {
  const byId = new Map(fields.map((f) => [f.id, f]));
  const shown = cols.visible.map((id) => byId.get(id)).filter(Boolean) as ListField<T>[];
  const accessors = Object.fromEntries(fields.map((f) => [f.id, f.value])) as Record<string, (r: T) => string | number | null | undefined>;
  const sorted = sort.sortRows(rows, accessors);

  const sumOf = (set: T[], id: string): number =>
    set.reduce((s, r) => { const v = byId.get(id)?.value(r); return s + (typeof v === "number" ? v : 0); }, 0);

  // Group when asked: keep the sorted order within each group.
  const groupField = cols.groupBy ? byId.get(cols.groupBy) : null;
  const groups: { key: string; rows: T[] }[] = [];
  if (groupField) {
    const m = new Map<string, T[]>();
    for (const r of sorted) {
      const k = fmtCell(groupField.value(r));
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    for (const [key, rs] of [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))) groups.push({ key, rows: rs });
  }

  const colCount = shown.length;
  const cell = (f: ListField<T>, r: T) => (f.render ? f.render(r) : fmtCell(f.value(r), f.numeric));

  return (
    <table className="w-full text-sm">
      <thead>
        <tr>
          {shown.map((f) => <Th key={f.id} id={f.id} sort={sort} align={f.numeric ? "right" : "left"}>{f.label}</Th>)}
        </tr>
      </thead>
      <tbody>
        {sorted.length === 0 ? (
          <tr><td colSpan={colCount} className="px-4 py-12 text-center text-[13.5px] font-medium text-slate-400">{emptyText}</td></tr>
        ) : groupField ? (
          groups.map((g) => (
            <GroupBlock
              key={g.key} groupKey={g.key} rows={g.rows} shown={shown} cols={cols}
              cell={cell} getKey={getKey} onRowClick={onRowClick} selectedKey={selectedKey} sumOf={sumOf}
            />
          ))
        ) : (
          sorted.map((r, i) => (
            <Row key={getKey(r)} r={r} idx={i} shown={shown} cell={cell} onRowClick={onRowClick} selected={selectedKey === getKey(r)} />
          ))
        )}
      </tbody>
      {cols.totals.length > 0 && sorted.length > 0 && (
        <tfoot>
          <tr className="border-t-2 border-slate-200 bg-slate-50 text-[12.5px] font-bold text-slate-800">
            {shown.map((f, i) => (
              <td key={f.id} className={cn("px-3 py-2", f.numeric && "text-right tabular-nums")}>
                {i === 0 ? `${sorted.length.toLocaleString()} total` : cols.totals.includes(f.id) ? fmtCell(sumOf(sorted, f.id), true) : ""}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  );
}

function Row<T>({ r, shown, cell, onRowClick, selected, idx = 0 }: {
  r: T; shown: ListField<T>[]; cell: (f: ListField<T>, r: T) => React.ReactNode;
  onRowClick?: (r: T) => void; selected?: boolean; idx?: number;
}) {
  return (
    <tr
      onClick={onRowClick ? () => onRowClick(r) : undefined}
      className={cn(
        "border-b border-slate-100 transition-colors",
        onRowClick && "cursor-pointer",
        // Subtle zebra aids cross-row tracking on wide financial tables; hover +
        // selected always win over it.
        selected ? "bg-brand-100/70" : cn(idx % 2 === 1 && "bg-slate-50/50", "hover:bg-brand-50/60"),
      )}
    >
      {shown.map((f) => (
        <td
          key={f.id}
          className={cn(
            // whitespace-nowrap = consistent single-line row heights (no ragged
            // wrapping like "Lennox Industries Inc." spilling to 3 lines).
            "whitespace-nowrap px-4 py-2.5 text-slate-700",
            f.numeric && "text-right tabular-nums",
          )}
        >
          {cell(f, r)}
        </td>
      ))}
    </tr>
  );
}

function GroupBlock<T>({ groupKey, rows, shown, cols, cell, getKey, onRowClick, selectedKey, sumOf }: {
  groupKey: string; rows: T[]; shown: ListField<T>[]; cols: ColumnState;
  cell: (f: ListField<T>, r: T) => React.ReactNode; getKey: (r: T) => string;
  onRowClick?: (r: T) => void; selectedKey?: string | null; sumOf: (set: T[], id: string) => number;
}) {
  const totalsBits = cols.totals
    .map((id) => { const f = shown.find((x) => x.id === id); return f ? `${f.label} ${fmtCell(sumOf(rows, id), true)}` : null; })
    .filter(Boolean);
  return (
    <>
      <tr className="bg-slate-100/90">
        <td colSpan={shown.length} className="border-y border-slate-200 px-4 py-2 text-[12.5px] font-bold text-slate-700">
          {groupKey} <span className="font-medium text-slate-400">· {rows.length}</span>
          {totalsBits.length > 0 && <span className="font-medium text-emerald-600"> · {totalsBits.join(" · ")}</span>}
        </td>
      </tr>
      {rows.map((r, i) => (
        <Row key={getKey(r)} r={r} idx={i} shown={shown} cell={cell} onRowClick={onRowClick} selected={selectedKey === getKey(r)} />
      ))}
    </>
  );
}
